"use client";

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import React, { useState, Fragment } from 'react'
import { Dialog, RadioGroup, Tab, Transition } from '@headlessui/react'
import {
  HomeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { StarIcon } from '@heroicons/react/20/solid'
import Image from "next/image"
import Link from "next/link";

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function ProductDetailBody({ product }: { product: any }) {
  const [open, setSizechartOpen] = useState(false)

  const handleSizechartClick = () => {
    setSizechartOpen(!open);
  };

  // Normalize API fields so intermittent nulls cannot crash SSR.
  const productImages = Array.isArray(product?.product_images) ? product.product_images : [];
  const reviews = Array.isArray(product?.reviews) ? product.reviews : [];
  const recommendedProducts = Array.isArray(product?.recommended_products)
    ? product.recommended_products
    : [];
  // complement = curated pairs (under gallery); related = range groups then category fallback
  const pairProducts = recommendedProducts.filter((p) => p?.relation === 'complement');
  const relatedProducts = recommendedProducts.filter(
    (p) => p?.relation === 'range' || p?.relation === 'fallback' || !p?.relation
  );
  const sizeOptions = Array.isArray(product?.qty_options?.options)
    ? product.qty_options.options
    : [];
  const categories = Array.isArray(product?.categories) ? product.categories : [];
  const colors = Array.isArray(product?.colors) ? product.colors : [];

  // Derive initial color / images from the product prop. `product` comes from
  // the server page and is stable for the lifetime of this mount (navigating
  // to a different slug remounts the page).
  const initialColor = product.is_builder_product
    ? ''
    : (colors[0]?.value ?? '');

  const initialPrimaryImage = product.is_builder_product
    ? (product?.primary_image ?? { thumb_url: '', large_url: '' })
    : (productImages.find(p => p.color_code == initialColor && p.image_type == 'sub')
        ?? product?.primary_image
        ?? { thumb_url: '', large_url: '' });

  const initialProductsImages = product.is_builder_product
    ? productImages
    : productImages.filter(p => p.color_code == initialColor && p.image_type == 'face');

  const [selectedColor, setSelectedColor] = useState<string>(initialColor)
  const [primaryImage, setPrimaryImage] = useState<{ thumb_url: string, large_url: string }>(initialPrimaryImage);
  const [selectedSize, setSelectedSize] = useState(null)
  const [productsImages, setProductsImages] = useState<any[]>(initialProductsImages);

  function selectColor(color){
    if(product.is_builder_product) return
    setSelectedColor(color || '')
    setPrimaryImage(productImages.find(p => p.color_code == color && p.image_type == 'sub') || product?.primary_image)
    setProductsImages(productImages.filter(p => p.color_code == color && p.image_type == 'face'))
  }

  // this.primary_image = {...clr.images.filter(e => e.image_type == 'sub')[0]}
  // this.product_images = [...clr.images.filter(e => e.image_type == 'face')]

  

  const pages = [
    { name: 'Catalog', href: '/catalog', current: false },
    { name: categories[0]?.title , href: `/catalog/${categories[0]?.slug}`, current: false },
    { name: product?.title , href: "#", current: true },
  ]

  return (
    <>
      <main className="mx-auto max-w-7xl sm:px-6 pt-10 sm:pt-16 lg:px-8">
        
        <div className="mx-auto max-w-2xl lg:max-w-none">
          <nav className="flex pt-8" aria-label="Breadcrumb">
            <ol role="list" className="flex items-center space-x-4 px-2">
              <li>
                <div>
                  <a href="#" className="text-gray-400 hover:text-gray-500">
                    <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <span className="sr-only">Home</span>
                  </a>
                </div>
              </li>
              {pages.map((page) => (
                <li key={page.name}>
                  <div className="flex items-center">
                    <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                    <Link
                      href={page.href}
                      className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                      aria-current={page.current ? 'page' : undefined}
                    >
                      {page.name}
                    </Link>
                  </div>
                </li>
              ))}
            </ol>
          </nav>
          {/* Product */}
          <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8 pb-8 pt-8">
            {/* Image gallery */}
            <Tab.Group as="div" className="flex flex-col-reverse">
              {/* Image selector */}
              <div className="mx-auto mt-6 hidden w-full max-w-2xl sm:block lg:max-w-none">
                <Tab.List className="grid grid-cols-4 gap-6">
                  <Tab
                    key="primary_image"
                    className="relative flex h-24 cursor-pointer items-center justify-center rounded-md bg-white text-sm font-medium uppercase text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-opacity-50 focus:ring-offset-4"
                  >
                    {({ selected }) => (
                      <>
                        {/* <span className="sr-only"> {image.name} </span> */}
                        <span className="absolute inset-0 overflow-hidden rounded-md">
                          {primaryImage?.thumb_url &&  <Image src={primaryImage?.thumb_url} width={250} height={250} alt={product.title} className="h-full w-full object-cover object-center" />}
                        </span>
                        <span
                          className={classNames(
                            selected ? 'ring-blue-500' : 'ring-transparent',
                            'pointer-events-none absolute inset-0 rounded-md ring-2 ring-offset-2'
                          )}
                          aria-hidden="true"
                        />
                      </>
                    )}
                  </Tab>
                  {productsImages?.map((image) => (
                    <Tab
                      key={`thumb_url-${image.id}`}
                      className="relative flex h-24 cursor-pointer items-center justify-center rounded-md bg-white text-sm font-medium uppercase text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-opacity-50 focus:ring-offset-4"
                    >
                      {({ selected }) => (
                        <>
                          <span className="sr-only"> {image.name} </span>
                          <span className="absolute inset-0 overflow-hidden rounded-md">
                            {image?.thumb_url && <Image src={image?.thumb_url} alt={product.title} width={250} height={250} className="h-full w-full object-cover object-center" />}
                          </span>
                          <span
                            className={classNames(
                              selected ? 'ring-blue-500' : 'ring-transparent',
                              'pointer-events-none absolute inset-0 rounded-md ring-2 ring-offset-2'
                            )}
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </Tab>
                  ))}
                </Tab.List>
              </div>

              <Tab.Panels className="aspect-w-1 aspect-h-1 w-full">
                <Tab.Panel key="primary_image">
                  {primaryImage?.large_url && <Image
                    src={primaryImage?.large_url}
                    alt={product.title}
                    width={650}
                    priority
                    height={650}
                    className="h-full w-full object-cover object-center sm:rounded-lg"
                  />}
                </Tab.Panel>
                {productsImages?.map((image) => (
                  <Tab.Panel key={image.id}>
                    {image?.large_url && <Image
                      src={image?.large_url}
                      width={650}
                      height={650}
                      alt={product.title}
                      className="h-full w-full object-cover object-center sm:rounded-lg"
                    />}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>

            {/* Product info */}
            <div className="mt-10 px-4 sm:mt-16 sm:px-0 lg:mt-0">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">{product.title}</h1>
              {/* <p className="text-gray-600 text-lg"> {product.sku} </p> */}
              {/* <div className="mt-3">
                <h2 className="sr-only">Product information</h2>
                <p className="text-3xl tracking-tight text-gray-900">{product.price}</p>
              </div> */}

              {/* Reviews */}
              <div className="mt-2">
                <h2 className="sr-only">Reviews</h2>
                <div className="flex items-center">
                  <div className="flex items-center">
                    {[0, 1, 2, 3, 4].map((rating) => (
                      <StarIcon
                        key={rating}
                        className={classNames(
                          product.average_rating_score > rating ? 'text-yellow-400' : 'text-gray-300',
                          'h-5 w-5 flex-shrink-0'
                        )}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <p className="ml-2 mt-1 text-sm text-gray-500">{product.total_reviews} reviews</p>
                  {/* <p className="sr-only">{product.average_rating_score} out of 5 stars</p> */}
                </div>
              </div>

              <div className="mt-6">
                <h2 className="sr-only">Description</h2>

                <div
                  className="space-y-6 text-base text-gray-700"
                  // dangerouslySetInnerHTML={{ __html: product.description }}
                >
                  <ReactMarkdown>{product.description}</ReactMarkdown>
                </div>
              </div>

              {/* Colors */}
              <div className="mt-6">
                <h2 className="text-sm font-medium text-gray-900">Color</h2>

                <RadioGroup value={selectedColor} onChange={selectColor} className="mt-2">
                  <RadioGroup.Label className="sr-only"> Choose a color </RadioGroup.Label>
                  <div className="flex items-center flex-wrap -m-1">
                    {colors.map((color, index) => (
                      <RadioGroup.Option
                        key={index}
                        value={color.value}
                        className={({ active, checked }) =>
                          classNames(
                            color.value,
                            !product.is_builder_product ? 'cursor-pointer' : '',
                            active && checked ? 'ring ring-offset-1' : '',
                            !active && checked ? 'ring-4' : '',
                            'm-1 relative flex items-center justify-center rounded-full focus:outline-none'
                          )
                        }
                      >
                        <RadioGroup.Label as="span" className="sr-only">
                          {' '}
                          {color.title}{' '}
                        </RadioGroup.Label>
                        <span
                          aria-hidden="true"
                          style={{backgroundColor: `${color.value}`}}
                          className='h-8 w-8 rounded-full border border-black border-opacity-10'
                        />
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>

              {/* Size picker */}
              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-gray-900">Size</h2>
                  <a href="#" onClick={handleSizechartClick} className="text-sm font-medium text-blue-600 hover:text-blue-500">
                    Size Chart
                  </a>
                </div>

                <RadioGroup value={selectedSize} onChange={setSelectedSize} className="mt-2">
                  <RadioGroup.Label className="sr-only">Choose a size</RadioGroup.Label>
                  <div className="grid grid-cols-4 gap-3 sm:grid-cols-7">
                    {sizeOptions.map((size) => (
                      <RadioGroup.Option
                        key={size}
                        value={size}
                        className={({ active, checked }) =>
                          classNames(
                            'cursor-pointer focus:outline-none',
                            active ? 'ring-2 ring-blue-500 ring-offset-2' : '',
                            checked
                              ? 'border-transparent bg-blue-600 text-white hover:bg-blue-700'
                              : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
                            'flex items-center justify-center rounded-md border py-3 px-3 text-sm font-medium uppercase sm:flex-1'
                          )
                        }
                        // disabled={!size.inStock}
                      >
                        <RadioGroup.Label as="span">{size}</RadioGroup.Label>
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              
              <div className="mt-10 flex">
                <Link
                  href="/contact-us"
                  className="flex max-w-xs flex-1 items-center justify-center rounded-md border border-transparent bg-blue-600 py-3 px-8 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-50 sm:w-full"
                >
                  Get A Quote
                </Link>

                {/* <button
                  type="button"
                  className="ml-4 flex items-center justify-center rounded-md py-3 px-3 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                >
                  <HeartIcon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
                  <span className="sr-only">Add to favorites</span>
                </button> */}
              </div>

              <section aria-labelledby="details-heading" className="mt-5">
                {pairProducts.length > 0 && (
                  <div className="border-gray-200 py-5" aria-labelledby="pair-heading">
                    <label id="pair-heading" className="text-sm font-medium text-gray-900">
                      Complete the set
                    </label>
                    <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                      {pairProducts.map((pair) => (
                        <li key={pair.slug}>
                          <Link href={`/products/${pair.slug}`} className="group block border border-gray-200 rounded-md p-2">
                            <div className="aspect-square overflow-hidden rounded-md bg-gray-100">
                              {pair.image_url && (
                                <Image
                                  src={pair.image_url}
                                  alt={pair.title}
                                  width={200}
                                  height={200}
                                  className="h-full w-full object-cover object-center group-hover:opacity-90"
                                />
                              )}
                            </div>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{pair.title}</h3>

                            {/* <p className="mt-2 text-xs font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600">
                              {pair.title}
                            </p> */}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className={pairProducts.length > 0 ? 'border-t border-gray-200 py-5' : 'border-gray-200 py-5'}>
                  <label className="text-sm font-medium text-gray-900">Brand</label>
                  <span className="ml-2 space-x-2 space-y-2 prose-sm mt-4 text-gray-500">
                    {product.brand?.name}
                  </span>
                </div>

                <div className="border-t border-gray-200 py-5">
                  <label className="text-sm font-medium text-gray-900">Categories</label>
                  <span className="ml-2 space-x-2 space-y-2 prose-lg mt-4 text-gray-500">
                    {categories.map((category) => (
                      <Link  key={category.slug} href={`/catalog/${category.slug}`} target="_blank">
                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-600">
                          {category.title}
                        </span>
                      </Link>
                    ))}
                  </span>
                </div>

                <div className="border-t border-gray-200 py-5">
                  <label className="text-sm font-medium text-gray-900">Tags</label>
                  <span className="ml-2 space-x-2 space-y-2 prose-lg mt-4 text-gray-500">
                    {product.tags?.map((tag) => (
                      <Link  key={tag} href={`/catalog?tags=${tag}`} target="_blank">
                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-600">
                          {tag}
                        </span>
                      </Link>
                    ))}
                  </span>
                </div>
                <Transition.Root show={open} as={Fragment}>
                  <Dialog as="div" className="relative z-10" onClose={setSizechartOpen}>
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-300"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="ease-in duration-200"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                          as={Fragment}
                          enter="ease-out duration-300"
                          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                          enterTo="opacity-100 translate-y-0 sm:scale-100"
                          leave="ease-in duration-200"
                          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                            <div className="prose prose-sm pb-6">
                              <h3 className="text-gray-900 text-lg mb-4"> Size Chart </h3>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{product.size_chart}</ReactMarkdown>
                            </div>
                            <div className="">
                              <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                onClick={() => setSizechartOpen(false)}
                              >
                                Close
                              </button>
                            </div>
                          </Dialog.Panel>
                        </Transition.Child>
                      </div>
                    </div>
                  </Dialog>
                </Transition.Root>
{/* 
                <div className="divide-y divide-gray-200 border-t">
                  <Disclosure as="div" key="sizechart">
                    {({ open }) => (
                      <>
                        <h3>
                          <Disclosure.Button className="group relative flex w-full items-center justify-between py-6 text-left">
                            <span
                              className={classNames(
                                open ? 'text-blue-600' : 'text-gray-900',
                                'text-sm font-medium'
                              )}
                            >
                              Size Chart
                            </span>
                            <span className="ml-6 flex items-center">
                              {open ? (
                                <MinusIcon
                                  className="block h-6 w-6 text-blue-400 group-hover:text-blue-500"
                                  aria-hidden="true"
                                />
                              ) : (
                                <PlusIcon
                                  className="block h-6 w-6 text-gray-400 group-hover:text-gray-500"
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          </Disclosure.Button>
                        </h3>
                        <Disclosure.Panel as="div" className="prose prose-sm pb-6">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{product.size_chart}</ReactMarkdown>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div> */}
              </section>
            </div>
          </div>

          
          <section aria-labelledby="reviews-heading" className="bg-white">
            <div className="border-t border-gray-200 mx-auto max-w-2xl py-12 px-4 sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-12 lg:gap-x-8 lg:py-16 lg:px-8">
              <div className="lg:col-span-4">
                <h2 id="reviews-heading" className="text-2xl font-bold tracking-tight text-gray-900">
                  Customer Reviews
                </h2>

                <div className="mt-3 flex items-center">
                  <div>
                    <div className="flex items-center">
                      {[0, 1, 2, 3, 4].map((rating) => (
                        <StarIcon
                          key={`rating-${rating}`}
                          className={classNames(
                            product.average_rating_score > rating ? 'text-yellow-400' : 'text-gray-300',
                            'h-5 w-5 flex-shrink-0'
                          )}
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                    <p className="sr-only">{product.average_rating_score} out of 5 stars</p>
                  </div>
                  {/* <p className="ml-2 mt-1 text-sm text-gray-500">{product.total_reviews} reviews</p> */}
                  <p className="ml-2 mt-1 text-sm text-gray-900">Based on {product.total_reviews} reviews</p>
                </div>

                <div className="mt-10">
                  <h3 className="text-lg font-medium text-gray-900">Share your thoughts</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    If you’ve used this product, share your thoughts with other customers
                  </p>

                  <Link
                    href="/contact-us"
                    className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white py-2 px-8 text-sm font-medium text-gray-900 hover:bg-gray-50 sm:w-auto lg:w-full"
                  >
                    Write a review
                  </Link>
                </div>
              </div>

              <div className="mt-16 lg:col-span-7 lg:col-start-6 lg:mt-0">
                <h2 className="sr-only">Recent reviews</h2>

                <div className="flow-root">
                  <div className="divide-y divide-gray-200">
                    {reviews.map((review, index) => (
                      <div key={`${index}-${review.author}`} className="py-4">
                        <div className="flex items-center">
                          {/* <img src="/logo.png" alt={`${review.author}.`} className="h-12 w-12 rounded-full" /> */}
                          <div className="">
                            <h3 className="text-sm font-bold text-gray-900">{review.author || 'Anonymous user'}</h3>
                            <div className="mt-1 flex items-center">
                              {[0, 1, 2, 3, 4].map((rating) => (
                                <StarIcon
                                  key={rating}
                                  className={classNames(
                                    review.rating > rating ? 'text-yellow-400' : 'text-gray-300',
                                    'h-5 w-5 flex-shrink-0'
                                  )}
                                  aria-hidden="true"
                                />
                              ))}
                            </div>
                            <p className="sr-only">{review.rating} out of 5 stars</p>
                          </div>
                        </div>

                        <div
                          className="mt-4 space-y-6 text-base italic text-gray-600"
                          dangerouslySetInnerHTML={{ __html: review.content }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {relatedProducts.length > 0 && (
          <section aria-labelledby="related-heading" className="border-t border-gray-200 py-12 px-4 sm:px-0">
            <h2 id="related-heading" className="text-xl font-bold text-gray-900">
              Related Products
            </h2>

            <div className="mt-8 grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
              {relatedProducts.map((related) => (
                <Link key={related.slug} href={`/products/${related.slug}`}>
                  <div className="relative">
                    <div className="relative h-72 w-full overflow-hidden rounded-lg">
                      {related.image_url && <Image
                        src={related.image_url}
                        alt={related.title}
                        width={500}
                        height={500}
                        className="h-full w-full object-cover object-center"
                      />}
                    </div>
                    <div className="relative mt-4">
                      <h3 className="text-sm font-medium text-gray-900">{related.title}</h3>
                    </div>
                    <div className="absolute inset-x-0 top-0 flex h-72 items-end justify-end overflow-hidden rounded-lg p-4">
                      <div
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black opacity-50"
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
          )}

        </div>
      </main>
    </>
  )
}
